/**
 * momijiLogoAnim 関数
 * .about-container 要素が存在する場合に、
 * アイコンがランダムな大きさ・角度・色で
 * フワフワとアニメーションしながら画面を横切る動きを実装する関数。
 */
export function momijiLogoAnim() {
  // .about-container クラスを持つ要素を取得
  const container = document.querySelector('.about-container');
  
  // .about-container が存在する場合のみ実行
  if (container) {

    /**
     * ベースパラメータ
     * 各種値の最小値～最大値をまとめたオブジェクト。
     * minSize: オブジェクトの最小サイズ (%)
     * maxSize: オブジェクトの最大サイズ (%)
     * minSpeed: アニメーションの最短時間 (ms)
     * maxSpeed: アニメーションの最長時間 (ms)
     * minInterval: オブジェクト生成間隔の最小値 (ms)
     * maxInterval: オブジェクト生成間隔の最大値 (ms)
     * minAmplitude: バウンスの最小振れ幅 (rem)
     * maxAmplitude: バウンスの最大振れ幅 (rem)
     * minBounceInterval: バウンスの最小時間間隔 (ms)
     * maxBounceInterval: バウンスの最大時間間隔 (ms)
     * minAngle: オブジェクトが移動する際の最小角度 (度)
     * maxAngle: オブジェクトが移動する際の最大角度 (度)
     * minStartYPercent: オブジェクトが生成される縦位置の最小割合 (%)
     * maxStartYPercent: オブジェクトが生成される縦位置の最大割合 (%)
     */
    const baseParams = {
      minSize: 10,
      maxSize: 25,
      minSpeed: 8000,
      maxSpeed: 12000,
      minInterval: 1000,
      maxInterval: 3000,
      minAmplitude: 1,
      maxAmplitude: 3,
      minBounceInterval: 300,
      maxBounceInterval: 1200,
      minAngle: 15,
      maxAngle: 30,
      minStartYPercent: 5,
      maxStartYPercent: 10
    };

    /**
     * ブレークポイントごとの追加パラメータ
     * 画面幅が特定の値以下の場合に、パラメータを上書きして調整する
     */
    const breakpoints = [
      {
        width: 768,
        params: {
          minSize: 20,
          maxSize: 40,
          minSpeed: 6000,
          maxSpeed: 10000,
          minInterval: 2500,
          maxInterval: 5500,
          minAngle: 10,
          maxAngle: 15,
          minStartYPercent: -2,
          maxStartYPercent: -2
        }
      }
    ];

    // 現在有効なパラメータを保持するオブジェクト。初期値はベースパラメータをコピー
    let currentParams = { ...baseParams };

    // オブジェクトを再利用（リサイクル）するためのプール
    const objectPool = [];

    // 同時に表示可能な最大オブジェクト数
    const maxConcurrentObjects = 20;

    // 現在表示されているオブジェクトの数
    let currentObjectCount = 0;

    // 最後にオブジェクトが生成された時刻（アニメーション間隔計測用）
    let lastSpawnTime = performance.now();

    /**
     * 画面サイズに応じて現在のパラメータを更新する関数
     * ベースパラメータを読み込みつつ、breakpoints の条件を満たしたら上書きする
     */
    function updateParams() {
      const width = window.innerWidth;
      
      // まずベースパラメータにリセットしておく
      currentParams = { ...baseParams };

      // ブレークポイントを順にチェックし、条件を満たすものがあればパラメータを上書き
      for (const breakpoint of breakpoints) {
        if (width <= breakpoint.width) {
          currentParams = { ...currentParams, ...breakpoint.params };
        }
      }
    }

    // 初回のパラメータ更新
    updateParams();

    // ウィンドウサイズ変更時にパラメータを更新
    window.addEventListener('resize', () => {
      updateParams();
    });

    /**
     * SVG の <symbol> 情報を保持するオブジェクト
     * key: symbol の id
     * value: viewBox など
     */
    const symbolData = {};

    // ページ上にあるすべての <svg> 内の <symbol> 要素を取得
    const symbols = document.querySelectorAll('svg symbol');

    // 各 symbol 要素から id と viewBox を取り出して記録
    symbols.forEach(symbol => {
      const id = symbol.getAttribute('id');
      const viewBox = symbol.getAttribute('viewBox');
      symbolData[id] = {
        viewBox: viewBox
      };
    });

    /**
     * オブジェクトを生成する関数
     * 1. 同時表示数制限を超えない場合のみ生成
     * 2. objectPool に既存の要素があれば再利用し、なければ新規生成
     * 3. 大きさや速度、アニメーションのパラメータをランダムに決めて適用
     * 4. コンテナに追加して、一定時間経過後に再びプールに返却
     */
    function createObject() {
      // 同時表示数が上限に達したら何もしない
      if (currentObjectCount >= maxConcurrentObjects) return;
      // カウントを増やす
      currentObjectCount++;

      let obj;
      // objectPool に要素が残っていれば再利用
      if (objectPool.length > 0) {
        obj = objectPool.pop();
        // 表示を再度有効化
        obj.style.display = 'block';
      } else {
        // 新規にオブジェクトを作成
        obj = document.createElement('div');
        obj.classList.add('object');
        
        // SVG 要素を作成
        const svgNS = "http://www.w3.org/2000/svg";
        const svgElem = document.createElementNS(svgNS, 'svg');
        svgElem.setAttribute('width', '100%');
        svgElem.setAttribute('height', '100%');

        // <use> 要素を作り、SVG に追加
        const useElem = document.createElementNS(svgNS, 'use');
        svgElem.appendChild(useElem);
        
        // SVG をオブジェクト (div) に追加
        obj.appendChild(svgElem);
      }

      // 現在のパラメータを分割代入で取得
      const {
        minSize,
        maxSize,
        minSpeed,
        maxSpeed,
        minAmplitude,
        maxAmplitude,
        minBounceInterval,
        maxBounceInterval,
        minAngle,
        maxAngle,
        minStartYPercent,
        maxStartYPercent,
      } = currentParams;

      // ---------------------------------------------
      // ここからパラメータをランダムに決めて obj に適用していく
      // ---------------------------------------------

      // 1. オブジェクトの大きさ（%）をランダムに決める
      const size = Math.random() * (maxSize - minSize) + minSize;
      obj.style.width = size + '%';
      obj.style.height = size + '%';

      // 2. アニメーション速度（ms）をランダムに決める
      const speed = Math.random() * (maxSpeed - minSpeed) + minSpeed;

      // 3. バウンス（上下動）の振れ幅をランダムに決める（単位 rem）
      const amplitudeValue = Math.random() * (maxAmplitude - minAmplitude) + minAmplitude;
      const amplitude = amplitudeValue + 'rem';

      // 4. バウンスが発生する間隔をランダムに決める（ms）
      const bounceInterval = Math.random() * (maxBounceInterval - minBounceInterval) + minBounceInterval;

      // 5. 総バウンス回数を計算（使っていないが参考にできる値）
      const totalBounces = Math.ceil(speed / bounceInterval);

      // 6. オブジェクトが移動する角度（度）をランダムに決める
      const angle = Math.random() * (maxAngle - minAngle) + minAngle;
      // ラジアンに変換
      const angleRad = angle * (Math.PI / 180);

      // 7. コンテナの幅・高さを取得（px）
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      // 8. オブジェクトのサイズ（px）
      //    containerWidth の何% か（size は % なので、実際のピクセルに変換）
      const sizeInPx = (size / 100) * containerWidth;

      // 9. アニメーション開始位置（縦方向）をランダムに決める
      //    minStartYPercent～maxStartYPercent の範囲で決定し、px に変換
      //    アイコンの中心がその位置に来るように調整（ - sizeInPx / 2 ）
      const startYPercent = Math.random() * (maxStartYPercent - minStartYPercent) + minStartYPercent;
      const startY = (startYPercent / 100) * containerHeight - sizeInPx / 2;
      obj.style.top = startY + 'px';

      // 10. アニメーション開始位置（横方向）は画面右端（= containerWidth）に設定
      obj.style.left = containerWidth + 'px';

      // 11. オブジェクトが左端まで移動する総距離を計算
      //     プラスで若干はみ出す部分も考慮し、(containerWidth + sizeInPx)
      const movementDistance = containerWidth + sizeInPx * 2;

      // 12. 終点の座標変化量を計算（角度で分解）
      const deltaX = -movementDistance * Math.cos(angleRad); // X 方向はマイナス
      const deltaY = movementDistance * Math.sin(angleRad);  // Y 方向は角度分を足す

      // 13. オブジェクトに CSS カスタムプロパティを設定（アニメーションで使用）
      obj.style.setProperty('--deltaX', `${deltaX}px`);
      obj.style.setProperty('--deltaY', `${deltaY}px`);
      obj.style.setProperty('--speed', `${speed}ms`);
      obj.style.setProperty('--amplitude', amplitude);
      obj.style.setProperty('--bounceInterval', `${bounceInterval}ms`);

      // ---------------------------------------------
      // SVG 関連の設定
      // ---------------------------------------------

      // 14. symbolData からランダムにシンボルを選択
      const symbolIDs = Object.keys(symbolData);
      const randomSymbolID = symbolIDs[Math.floor(Math.random() * symbolIDs.length)];
      const symbolInfo = symbolData[randomSymbolID];

      // 15. SVG の viewBox を設定
      const svgElem = obj.querySelector('svg');
      svgElem.setAttribute('viewBox', symbolInfo.viewBox);

      // 16. <use> 要素で参照するシンボルの ID を設定
      const useElem = svgElem.querySelector('use');
      useElem.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '#' + randomSymbolID);

      // 17. fill にランダムな色を設定
      //     例）'#abcdef' のような16進数カラーをランダムに生成
      const randomColor = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
      useElem.setAttribute('fill', randomColor);

      // ---------------------------------------------
      // オブジェクトを実際に画面上に追加
      // ---------------------------------------------
      container.appendChild(obj);

      /**
       * speed（ms）後に、以下を実行：
       * 1. currentObjectCount を減らす
       * 2. 要素を非表示にして container から削除
       * 3. プールに戻して再利用可能にする
       */
      setTimeout(() => {
        currentObjectCount--;
        obj.style.display = 'none';
        container.removeChild(obj);
        objectPool.push(obj);
      }, speed);
    }

    /**
     * アニメーションループ
     * requestAnimationFrame で毎フレーム呼ばれる
     * ランダムな間隔（minInterval～maxInterval）ごとに createObject() を呼び出す
     */
    function animationLoop() {
      const now = performance.now();
      // ランダムな生成間隔を決定
      const interval = Math.random() * (currentParams.maxInterval - currentParams.minInterval) + currentParams.minInterval;

      // 最後の生成時刻から interval（ms）経過していれば、新しいオブジェクトを生成
      if (now - lastSpawnTime >= interval) {
        createObject();
        lastSpawnTime = now;
      }

      // 次のフレームで再度呼び出す
      requestAnimationFrame(animationLoop);
    }

    if(document.body.classList.contains('template-home')) {
      setTimeout(() => {
        document.body.classList.add('opening-in')
      }, 200)
      setTimeout(() => {
        document.body.classList.add('opening-out')
      }, 2000)
      setTimeout(() => {
        // アニメーション開始
        animationLoop();
      }, 4000)
    } else {
      // アニメーション開始
      animationLoop();
    }
  }
}